"use client";

import { defaultLocale } from "@/config";
import { useChannel } from "@/context/channel";
import { getCollectionProducts } from "@/lib/api/product";
import { formatPrice } from "@/lib/utils";
import { filterCateImg } from "@/components/Home2/Collection";
import { Link } from "@/navigation";
import Price from "@/components/Price/price";
import { Skeleton } from "antd";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import Image from "next/image";

type ProductEdge = { node: { slug: string; [key: string]: any } };

const TABS = ["home-one"] as const;

function makeMeta(url: string) {
	return [{ key: "media", value: JSON.stringify([{ url }, { url }, { url }]) }];
}

const DEFAULT_PRODUCTS: ProductEdge[] = [
	{
		node: {
			slug: "",
			name: "80K Ultrasonic Slimming Machine",
			metadata: makeMeta("/image/gykj/products/减肥类/80K超声/六合一80k/2.jpg"),
			variants: [],
		},
	},
	{
		node: {
			slug: "",
			name: "360° Eye Bag RF Beauty Device",
			metadata: makeMeta("/image/gykj/products/减肥类/物理理疗/360眼袋仪+射频/微信图片_20230522112525.jpg"),
			variants: [],
		},
	},
	{
		node: {
			slug: "",
			name: "Portable Pico Laser Device",
			metadata: makeMeta("/image/gykj/products/激光类/纹身祛除/16.手持小皮秒/2.jpg"),
			variants: [],
		},
	},
	{
		node: {
			slug: "",
			name: "810nm Diode Laser Hair Removal",
			metadata: makeMeta("/image/gykj/products/激光类/脱毛/810nm脱毛仪/1.jpg"),
			variants: [],
		},
	},
];

export default function FeaturedProducts({ locale }: { locale: string }) {
	const t = useTranslations("homeBeauty");
	const { channel } = useChannel(locale);
	const [products, setProducts] = useState<ProductEdge[] | null>(null);

	useEffect(() => {
		if (!channel) return;
		getCollectionProducts({ slug: TABS[0], locale, channel }).then((res) => {
			setProducts(res?.collection?.products?.edges ?? []);
		});
	}, [channel, locale]);

	return (
		<section className="w-full bg-white py-16 sm:py-20 md:py-24">
			<div className="mx-auto w-full max-w-[1580px] px-5 sm:px-8 lg:px-12">
				<header className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
					<div>
						<span className="font-body text-brand-fg inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.2em] xl:text-[13px]">
							<span aria-hidden className="bg-brand inline-block h-px w-8" />
							{t("selectedEyebrow")}
						</span>
						<h2 className="font-display text-brand-ink mt-4 text-[clamp(32px,3.6vw,60px)] leading-[1.1]">
							{t("selectedTitle")}
						</h2>
					</div>
					<Link
						href="/products"
						className="border-brand-ink/42 font-body text-brand-ink hover:border-brand hover:!text-brand-fg group inline-flex items-center gap-2 border-b pb-1 text-[13px] font-semibold uppercase tracking-[0.22em] transition-colors duration-200 xl:text-[14px]"
					>
						{t("selectedViewAll")}
						<span aria-hidden className="transition-transform duration-300 group-hover:translate-x-1">
							→
						</span>
					</Link>
				</header>

				{products === null ? (
					<div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-4 lg:gap-6">
						{Array.from({ length: 4 }).map((_, i) => (
							<div key={i} className="flex flex-col gap-3">
								<div className="bg-brand-softer/40 aspect-square animate-pulse" />
								<Skeleton active paragraph={false} title={{ width: "70%" }} />
							</div>
						))}
					</div>
				) : (
					<div className="mt-12 grid grid-cols-2 gap-5 md:grid-cols-4 lg:gap-6">
						{(products.length === 0 ? DEFAULT_PRODUCTS : products.slice(0, 8)).map((item, idx) => (
							<ProductCard key={item.node.slug || idx} product={item.node} locale={locale} index={idx} />
						))}
					</div>
				)}
			</div>
		</section>
	);
}

function ProductCard({ product, locale, index }: { product: any; locale: string; index: number }) {
	const t = useTranslations("homeBeauty");
	const name = locale === defaultLocale ? product.name : product.translation?.name || product.name;
	const imgUrl = filterCateImg(product.metadata)[2]?.url || "/image/default-image.webp";
	const variant = product.variants?.[0];
	const price = variant?.pricing?.price?.gross?.amount as number | undefined;
	const priceCurrency = variant?.pricing?.price?.gross?.currency as string | undefined;
	const compareAt = price ? Number((price * 1.18).toFixed(2)) : undefined;
	const discount = price && compareAt ? Math.round(((compareAt - price) / compareAt) * 100) : 0;

	return (
		<Link
			href={product.slug ? `/product/${product.slug}` : "/products"}
			className="text-brand-ink hover:!text-brand-ink group flex flex-col"
		>
			<div className="relative flex aspect-square w-full items-center justify-center overflow-hidden bg-[#ffecd0]">
				{index < 2 && (
					<span className="bg-brand-ink font-body absolute left-3 top-3 z-10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
						{index === 0 ? t("productBadgeBestSeller") : t("productBadgeHot")}
					</span>
				)}
				<Image
					src={imgUrl}
					alt={name}
					width={500}
					height={500}
					sizes="(max-width:768px) 50vw, 25vw"
					className="h-full w-full object-contain transition-transform duration-700 ease-out group-hover:scale-[1.06]"
					quality={80}
				/>
			</div>
			<div className="mt-3 flex flex-col gap-1">
				<h3 className="font-body text-brand-ink/80 line-clamp-1 text-[15px] font-medium tracking-wide">
					{name}
				</h3>
				{price && (
					<div className="font-body text-brand-ink flex items-center gap-2 text-[17px] font-semibold tracking-tight">
						<Price price={price} currency={priceCurrency} />
						{compareAt && (
							<span className="text-brand-ink/35 text-[14px] font-medium line-through">
								{priceCurrency ?? "USD"} {formatPrice(String(compareAt))}
							</span>
						)}
						{discount > 0 && (
							<span className="bg-brand px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
								-{discount}%
							</span>
						)}
					</div>
				)}
			</div>
		</Link>
	);
}
